import React, { useMemo } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
    Tooltip,
    Avatar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Dashboard as DashboardIcon,
    Inventory as ProductIcon,
    ShoppingCart as OrderIcon,
    Store as MarketplaceIcon,
    Business as OMSIcon,
    Assessment as AnalyticsIcon,
    Description as ReportIcon,
    SupportAgent as SupportIcon,
    LocalShipping as ShippingIcon,
    MonetizationOn as BillingIcon,
    Person as PersonIcon,
    Settings as SettingsIcon,
    PriceChange as PriceChangeIcon,
    CloudSync as CMSIcon,
    Sort as SortIcon,
    AccountTree as WorkflowsIcon,
    Receipt as InvoiceIcon,
    Inventory2 as InventoryIcon,
    Search as SearchIcon
} from '@mui/icons-material';

/**
 * SidebarClient - Dedicated Navigation for Client Users
 * 
 * Provides streamlined access to client-facing features:
 * - Dashboard (Order tracking, KPIs)
 * - Orders (History, Status)
 * - Products (Catalog, Stock)
 * - Marketplace (Connections)
 * - Support & Billing
 * 
 * Follows Aurora 2026 design standards (consistent with SidebarWMS).
 */
const SidebarClient = ({ collapsed = false }) => {
    const { t } = useTranslation('client');
    const navigate = useNavigate();
    const location = useLocation();

    // Determine basic active state
    const isActiveLink = (path) => location.pathname.startsWith(path);

    const menuItems = useMemo(() => [
        {
            id: 'dashboard',
            label: t('nav.dashboard', 'My Dashboard'),
            path: '/client/dashboard',
            icon: <DashboardIcon />,
            color: '#00e5ff'
        },
        {
            id: 'search',
            label: t('nav.search', 'Search'),
            path: '/client/search',
            icon: <SearchIcon />,
            color: '#78909c'
        },
        { type: 'divider', label: 'My Operations' },
        {
            id: 'orders',
            label: t('nav.orders', 'My Orders'),
            path: '/client/orders',
            icon: <OrderIcon />,
            color: '#2196f3'
        },
        {
            id: 'expeditions',
            label: t('nav.expeditions', 'Shipments'),
            path: '/client/expeditions',
            icon: <ShippingIcon />,
            color: '#e91e63'
        },
        {
            id: 'tms',
            label: t('nav.tms', 'TMS'),
            path: '/client/tms',
            icon: <ShippingIcon />,
            color: '#795548'
        },
        {
            id: 'ttms-trading',
            label: t('nav.ttmsTrading', 'TTMS Trading'),
            path: '/client/ttms-trading',
            icon: <PriceChangeIcon />,
            color: '#ff5722'
        },
        {
            id: 'products',
            label: t('nav.products', 'My Products'),
            path: '/client/products',
            icon: <ProductIcon />,
            color: '#4caf50'
        },
        {
            id: 'inventory',
            label: t('nav.inventory', 'Inventory'),
            path: '/client/inventory',
            icon: <InventoryIcon />,
            color: '#8bc34a'
        },
        { type: 'divider', label: 'Network' },
        {
            id: 'marketplace',
            label: t('nav.marketplace', 'Marketplace'),
            path: '/client/marketplace',
            icon: <MarketplaceIcon />,
            color: '#ff9800'
        },
        {
            id: 'oms',
            label: t('nav.oms', 'OMS Connect'),
            path: '/client/oms',
            icon: <OMSIcon />,
            color: '#9c27b0'
        },
        {
            id: 'cms-integrations',
            label: t('nav.cmsIntegrations', 'CMS Integrations'),
            path: '/client/cms-integrations',
            icon: <CMSIcon />,
            color: '#673ab7'
        },
        {
            id: 'parcel-sorting',
            label: t('nav.parcelSorting', 'Parcel Sorting'),
            path: '/client/parcel-sorting',
            icon: <SortIcon />,
            color: '#00bcd4'
        },
        {
            id: 'workflows',
            label: t('nav.workflows', 'Workflows'),
            path: '/client/workflows',
            icon: <WorkflowsIcon />,
            color: '#607d8b'
        },
        { type: 'divider', label: 'Management' },
        {
            id: 'billing',
            label: t('nav.billing', 'Billing'),
            path: '/client/billing',
            icon: <BillingIcon />,
            color: '#009688'
        },
        {
            id: 'billing-estimate',
            label: t('nav.billingEstimate', 'Billing Estimate'),
            path: '/client/billing-estimate',
            icon: <PriceChangeIcon />,
            color: '#00796b'
        },
        {
            id: 'invoices',
            label: t('nav.invoices', 'Invoices'),
            path: '/client/portal/invoices',
            icon: <InvoiceIcon />,
            color: '#00897b'
        },
        {
            id: 'analytics',
            label: t('nav.analytics', 'Analytics'),
            path: '/client/reports',
            icon: <AnalyticsIcon />,
            color: '#3f51b5'
        },
        {
            id: 'support',
            label: t('nav.support', 'Support'),
            path: '/client/support-modern',
            icon: <SupportIcon />,
            color: '#f44336'
        },
        {
            id: 'profile',
            label: t('nav.profile', 'My Profile'),
            path: '/client/profile',
            icon: <PersonIcon />,
            color: '#546e7a'
        }
    ], [t]);

    const handleItemClick = (path) => {
        navigate(path);
    };

    return (
        <Box
            sx={{
                width: collapsed ? 80 : 280,
                height: '100vh',
                backgroundColor: '#1a237e', // Deep Indigo for Client Theme
                borderRight: '1px solid #283593',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 1200,
                boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: collapsed ? 2 : 3,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    gap: 2,
                    borderBottom: '1px solid #283593',
                    background: 'linear-gradient(180deg, #283593 0%, #1a237e 100%)'
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        bgcolor: 'secondary.main',
                        width: collapsed ? 40 : 36,
                        height: collapsed ? 40 : 36,
                        boxShadow: '0 0 15px rgba(255, 64, 129, 0.3)'
                    }}
                >
                    <PersonIcon />
                </Avatar>
                {!collapsed && (
                    <Box>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1, letterSpacing: '0.5px' }}>
                            CLIENT PORTAL
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#c5cae9', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            WMS Connector
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00e676' }} />
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Menu Items */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2 }}>
                <List component="nav" disablePadding>
                    {menuItems.map((item, index) => {
                        if (item.type === 'divider') {
                            return !collapsed && (
                                <Box key={`div-${index}`} sx={{ px: 3, py: 1.5, mt: 1 }}>
                                    <Typography variant="overline" sx={{ color: '#7986cb', fontWeight: 700, letterSpacing: '1px' }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            );
                        }

                        const isActive = isActiveLink(item.path);

                        return (
                            <Tooltip key={item.id} title={collapsed ? item.label : ''} placement="right">
                                <ListItem
                                    button
                                    onClick={() => handleItemClick(item.path)}
                                    sx={{
                                        mx: 1.5,
                                        my: 0.5,
                                        borderRadius: 2,
                                        width: 'auto',
                                        minHeight: 48,
                                        justifyContent: collapsed ? 'center' : 'flex-start',
                                        backgroundColor: isActive ? `${item.color}25` : 'transparent',
                                        borderLeft: isActive ? `3px solid ${item.color}` : '3px solid transparent',
                                        '&:hover': {
                                            backgroundColor: `${item.color}15`,
                                            '& .MuiListItemIcon-root': { color: item.color }
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: collapsed ? 0 : 40,
                                            justifyContent: 'center',
                                            color: isActive ? item.color : '#9fa8da',
                                            transition: 'color 0.2s ease'
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>

                                    {!collapsed && (
                                        <ListItemText
                                            primary={item.label}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    color: isActive ? '#fff' : '#c5cae9',
                                                    fontWeight: isActive ? 600 : 500,
                                                    fontSize: '0.9rem',
                                                    transition: 'color 0.2s ease'
                                                }
                                            }}
                                        />
                                    )}
                                </ListItem>
                            </Tooltip>
                        );
                    })}
                </List>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid #283593',
                    display: collapsed ? 'none' : 'block'
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#283593',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>Support Live</Typography>
                        <Typography variant="caption" sx={{ color: '#00e676' }}>Online 24/7</Typography>
                    </Box>
                    <Chip label="PRO" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#ff4081', color: '#fff' }} />
                </Box>
            </Box>
        </Box>
    );
};

export default React.memo(SidebarClient);
